/* HUENIT LAB 2.0 — Admin: 버전 관리.
   레지스트리 버킷(huenit-lab-registry)의 앱 설치파일(app/)·펌웨어(firmware/) 버전을
   열람·삭제·latest 변경(롤백)한다. 불필요한 구버전을 수동 정리해 스토리지 관리.
   목록/RMW 패턴은 features/examples 와 동일(repo.js 캡슐화). fake 모드(env 미설정)도
   동일 코드로 동작(registryBucket.js fake). 403(IAM 미적용)·401(세션만료)은 배너로 안내.
   패널은 5개씩 페이지네이션하고, 앱 설치파일은 플랫폼(Windows/macOS), 펌웨어는 기기
   type(로봇팔/AI 카메라)으로 좌우 분리한다 — 목록 fetch 는 각각 1회, 화면에서만 나눈다.
   새로고침은 패널마다 두지 않고 화면 최상단의 [전체 새로고침] 하나로 둘 다 다시 읽는다. */
import { useState, useRef, useEffect, useCallback } from 'react';
import { Icon } from '../../icons/Icon.jsx';
import { Modal, Confirm, useToast, Loading, EmptyState, ErrorState } from '../../components/index.js';
import { useSession } from '../../auth/session.jsx';
import {
  listApp, listFirmware, deleteRegistryObject, setAppLatest, setFirmwareLatest,
  fetchSidecarSha256, seedFake, uploadAppVersion, uploadFirmware, objectUrl, parseAppVersion,
  APP_PLATFORMS,
} from './repo.js';
import { fmtDateTime } from '../../lib/datetime.js';
import { downloadObject } from '../../lib/download.js';

function humanSize(bytes) {
  if (bytes == null) return '—';
  const u = ['B', 'KB', 'MB', 'GB']; let n = bytes, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return (i === 0 ? n : (n < 10 ? n.toFixed(2) : n.toFixed(1))) + ' ' + u[i];
}
/* 한 패널에 보여줄 행 수 — 넘치면 페이지 분리(앱·펌웨어 공통). */
const PAGE_SIZE = 5;

/* ============ 페이지 이동 ============ */
function Pager({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 12 }}>
      <button className="btn btn--ghost btn--sm" disabled={page <= 1} onClick={() => onPage(page - 1)}><Icon name="chev-l" size={14} />이전</button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
        <button key={n} className={"btn btn--sm" + (n === page ? " btn--primary" : " btn--ghost")} style={{ minWidth: 32 }} onClick={() => onPage(n)}>{n}</button>
      ))}
      <button className="btn btn--ghost btn--sm" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>다음<Icon name="chev-r" size={14} /></button>
    </div>
  );
}

/* ============ latest 변경(롤백) 모달 ============ */
function SetLatestModal({ row, kind, onSubmit, onClose, busy }) {
  const isFw = kind === 'firmware';
  // 파일명에서 버전 추정: app=...-<ver>-setup.exe, firmware=<ver>.bin
  const guessVersion = () => {
    if (isFw) { const m = row.name.match(/([0-9]+\.[0-9]+\.[0-9]+[^/]*)\.bin$/i); return m ? m[1] : ''; }
    // 앱: Huenit_Lab_<ver>_Setup.exe / Huenit_Lab_<ver>.dmg 에서 <ver> 파싱. 실패 시 느슨한 폴백.
    return parseAppVersion(row.name) || (row.name.match(/([0-9]+\.[0-9]+\.[0-9]+[A-Za-z0-9.\-]*)/) || [, ''])[1];
  };
  const [version, setVersion] = useState(guessVersion());
  const [notes, setNotes] = useState('');
  const [sha256, setSha256] = useState('');
  const [shaLoading, setShaLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setShaLoading(true);
    fetchSidecarSha256(row.key)
      .then(s => { if (!cancelled && s) setSha256(s); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setShaLoading(false); });
    return () => { cancelled = true; };
  }, [row.key]);

  const shaOk = /^[a-fA-F0-9]{64}$/.test(sha256.trim());
  const canSave = !busy && version.trim() && shaOk;

  return (
    <Modal title="latest 로 지정 (롤백)" sub={isFw ? '선택한 펌웨어를 해당 type 의 최신으로 manifest 에 기록합니다.' : '선택한 설치파일을 앱 최신 버전으로 manifest 에 기록합니다.'}
      onClose={busy ? () => {} : onClose}
      foot={<>
        <button className="btn btn--ghost" onClick={onClose} disabled={busy}>취소</button>
        <button className="btn btn--primary" disabled={!canSave} onClick={() => onSubmit({ version: version.trim(), notes, sha256: sha256.trim() })}>
          {busy ? '적용 중…' : 'latest 로 지정'}
        </button>
      </>}>
      <div className="field">
        <label className="field__label">대상 객체</label>
        <div className="mono" style={{ fontSize: 12.5, color: 'var(--ink-600)', wordBreak: 'break-all' }}>{row.key}</div>
        {isFw && <div className="field__hint" style={{ fontSize: 12, color: 'var(--ink-400)', marginTop: 4 }}>type: <b>{row.type || '(알 수 없음)'}</b></div>}
      </div>
      <div className="field">
        <label className="field__label">버전<span className="req">*</span></label>
        <input className="input" value={version} onChange={e => setVersion(e.target.value)} placeholder={isFw ? '예: 1.3.0' : '예: 0.6.0-beta.6'} />
      </div>
      <div className="field">
        <label className="field__label">sha256<span className="req">*</span></label>
        <input className="input mono" value={sha256} onChange={e => setSha256(e.target.value)}
          placeholder={shaLoading ? '.sha256 사이드카 확인 중…' : '64자리 hex (사이드카 없으면 직접 입력)'} />
        {!shaLoading && !shaOk && sha256.trim() !== '' && <div className="field__hint" style={{ fontSize: 12, color: 'var(--danger, #c0392b)', marginTop: 4 }}>sha256 은 64자리 16진수여야 합니다.</div>}
        {!shaLoading && sha256.trim() === '' && <div className="field__hint" style={{ fontSize: 12, color: 'var(--ink-400)', marginTop: 4 }}>사이드카(.sha256)를 찾지 못했습니다 — 직접 입력하세요.</div>}
      </div>
      <div className="field">
        <label className="field__label">릴리즈 노트</label>
        <input className="input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="선택 — manifest notes" />
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 10, background: 'var(--warn-bg)', color: '#8a1c2b', fontSize: 12.5, lineHeight: 1.55 }}>
        <Icon name="bell" size={16} />
        <span><b>되돌릴 수 없습니다.</b> 이 {isFw ? '펌웨어' : '설치파일'}을 latest 로 지정하면 manifest 가 갱신되어 <b>모든 사용자</b>에게 이 버전이 자동 업데이트로 안내됩니다.</span>
      </div>
    </Modal>
  );
}

/* ============ 업로드 파일 드롭존 ============ */
function UploadDrop({ accept, hint, fileName, onPick, onClear, disabled }) {
  const toast = useToast();
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const pick = (f) => {
    if (!f) return;
    const allowed = accept.toLowerCase().split(',').map((ext) => ext.trim()).filter(Boolean);
    if (!allowed.some((ext) => f.name.toLowerCase().endsWith(ext))) {
      toast(`${accept} 파일만 올릴 수 있습니다.`, 'error');
      return;
    }
    onPick(f);
  };
  if (fileName) {
    return (
      <div className="dropzone__file">
        <Icon name="file" size={18} />
        <span className="dropzone__file-name">{fileName}</span>
        {!disabled && <button type="button" className="icon-btn icon-btn--ghost" onClick={onClear}><Icon name="close" size={16} /></button>}
      </div>
    );
  }
  return (
    <div className={"dropzone" + (over ? " is-over" : "")}
      onClick={() => !disabled && inputRef.current && inputRef.current.click()}
      onDragOver={e => { e.preventDefault(); if (!disabled) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); if (!disabled) pick(e.dataTransfer.files[0]); }}>
      <div className="dropzone__icon"><Icon name="upload" size={22} /></div>
      <div className="dropzone__t">{accept} 파일을 끌어다 놓기</div>
      <div className="dropzone__d">{hint}</div>
      <input ref={inputRef} type="file" accept={accept} hidden disabled={disabled} onChange={e => pick(e.target.files[0])} />
    </div>
  );
}

/* ============ 앱 새 버전 등록 모달 ============ */
/* 플랫폼은 어느 패널의 [새 버전 등록] 을 눌렀는지로 정해진다(펌웨어 기기 종류와 같은 방식) —
   Windows manifest 에 .dmg 를 기록하는 사고를 화면 구조로 막는다. */
function AppUploadModal({ onSubmit, onClose, busy, step, platform }) {
  const spec = APP_PLATFORMS[platform] || APP_PLATFORMS.windows;
  const [fileObj, setFileObj] = useState(null);
  const [fileName, setFileName] = useState('');
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  // 파일명에서 버전 추정(...-<ver>-setup.exe).
  const pick = (f) => {
    setFileObj(f); setFileName(f.name);
    if (!version) { const m = f.name.match(/([0-9]+\.[0-9]+\.[0-9]+[A-Za-z0-9.\-]*)/); if (m) setVersion(m[1]); }
  };
  const canSave = !busy && fileObj && version.trim();
  return (
    <Modal title={`앱 새 버전 등록 — ${spec.label}`}
      sub={`설치파일을 ${spec.prefix} 에 올리고 ${spec.manifestKey} 를 새 latest 로 기록합니다.`}
      onClose={busy ? () => {} : onClose}
      foot={<>
        <button className="btn btn--ghost" onClick={onClose} disabled={busy}>취소</button>
        <button className="btn btn--primary" disabled={!canSave} onClick={() => onSubmit({ platform: spec === APP_PLATFORMS.mac ? 'mac' : 'windows', fileObj, fileName, version: version.trim(), notes })}>
          {busy ? (step || '업로드 중…') : '업로드'}
        </button>
      </>}>
      <div className="field">
        <label className="field__label">설치파일<span className="req">*</span></label>
        <UploadDrop accept={spec.accept}
          hint={spec === APP_PLATFORMS.mac ? '또는 클릭해서 선택 · macOS 설치본(.dmg/.pkg)' : '또는 클릭해서 선택 · Inno Setup 설치본'}
          fileName={fileName} disabled={busy}
          onPick={pick} onClear={() => { setFileObj(null); setFileName(''); }} />
      </div>
      <div className="field">
        <label className="field__label">버전<span className="req">*</span></label>
        <input className="input" value={version} onChange={e => setVersion(e.target.value)} placeholder="예: 0.6.0-beta.8" disabled={busy} />
        <div className="field__hint" style={{ fontSize: 12, color: 'var(--ink-400)', marginTop: 4 }}>객체 키: <span className="mono">{spec.prefix}{fileName || '<파일명>'}</span></div>
      </div>
      <div className="field">
        <label className="field__label">릴리즈 노트</label>
        <input className="input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="선택 — manifest notes" disabled={busy} />
      </div>
      {busy && <div className="field__hint" style={{ fontSize: 12.5, color: 'var(--ink-400)' }}>대용량 설치파일은 업로드에 시간이 걸릴 수 있습니다 — 창을 닫지 마세요.</div>}
    </Modal>
  );
}

/* ============ 펌웨어 새 버전 업로드 모달 ============ */
function FirmwareUploadModal({ onSubmit, onClose, busy, step, defaultType }) {
  // 기기 종류는 어느 패널의 [새 버전 업로드] 를 눌렀는지로 정해진다 — 화면이 이미 로봇팔/
  // AI 카메라로 나뉘어 있어 여기서 또 고르게 하면 패널과 어긋난 곳에 올릴 수 있다.
  const type = defaultType === 'camera' ? 'camera' : 'robot';
  const typeLabel = type === 'camera' ? 'AI 카메라' : '로봇팔';
  const [fileObj, setFileObj] = useState(null);
  const [fileName, setFileName] = useState('');
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const pick = (f) => {
    setFileObj(f); setFileName(f.name);
    if (!version) { const m = f.name.match(/([0-9]+\.[0-9]+\.[0-9]+[^/]*)\.bin$/i); if (m) setVersion(m[1]); }
  };
  const canSave = !busy && fileObj && version.trim();
  return (
    <Modal title={`펌웨어 새 버전 업로드 — ${typeLabel}`} sub={`firmware/${type}/{version}.bin 으로 올리고 manifest 의 ${type} entry 를 교체합니다.`}
      onClose={busy ? () => {} : onClose}
      foot={<>
        <button className="btn btn--ghost" onClick={onClose} disabled={busy}>취소</button>
        <button className="btn btn--primary" disabled={!canSave} onClick={() => onSubmit({ type, fileObj, version: version.trim(), notes })}>
          {busy ? (step || '업로드 중…') : '업로드'}
        </button>
      </>}>
      <div className="field">
        <label className="field__label">펌웨어 파일<span className="req">*</span></label>
        <UploadDrop accept=".bin" hint="또는 클릭해서 선택 · 펌웨어 바이너리" fileName={fileName} disabled={busy}
          onPick={pick} onClear={() => { setFileObj(null); setFileName(''); }} />
      </div>
      <div className="field">
        <label className="field__label">버전<span className="req">*</span></label>
        <input className="input" value={version} onChange={e => setVersion(e.target.value)} placeholder="예: 1.4.0" disabled={busy} />
        <div className="field__hint" style={{ fontSize: 12, color: 'var(--ink-400)', marginTop: 4 }}>객체 키: <span className="mono">firmware/{type}/{version || '<버전>'}.bin</span></div>
      </div>
      <div className="field">
        <label className="field__label">릴리즈 노트</label>
        <input className="input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="선택 — manifest notes" disabled={busy} />
      </div>
    </Modal>
  );
}

/* ============ 패널(앱/펌웨어 공통) ============ */
function VersionPanel({ title, icon, state, isDeveloper, onRetry, onRelogin, onDelete, onSetLatest, onUpload, onDownload, uploadLabel, emptySub, style }) {
  const data = state && state.data;
  // 검증중 후보 = manifest 가 아직 안 가리키는, 가장 최근 업로드된 설치파일(app 한정). rows 는 최신순.
  const candidateKey = data && data.kind === 'app' && data.rows[0] && !data.rows[0].isLatest ? data.rows[0].key : null;

  const rows = (data && data.rows) || [];
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  // 삭제·새로고침으로 행이 줄면 현재 페이지가 비어 버린다 — 마지막 페이지로 당긴다.
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <section className="card" style={{ background: '#fff', border: '1px solid var(--ink-100)', borderRadius: 14, padding: 16, marginBottom: 18, ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ display: 'inline-flex', width: 34, height: 34, borderRadius: 9, background: 'var(--brand-50)', color: 'var(--brand-700)', alignItems: 'center', justifyContent: 'center' }}><Icon name={icon} size={18} /></span>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15 }}>{title}</div>
          {data && (
            <div style={{ fontSize: 12, color: 'var(--ink-400)' }}>
              {rows.length}개 객체 · 합계 {humanSize(data.totalSize)}{totalPages > 1 ? ` · ${page}/${totalPages} 페이지` : ''}
            </div>
          )}
        </div>
        <div style={{ flex: 1 }} />
        {/* 새로고침은 패널마다 두지 않는다 — 화면 상단 [전체 새로고침] 하나가 앱·펌웨어를
            같이 다시 읽는다(어느 패널을 눌러도 같은 목록 fetch 라 중복이었다). */}
        {onUpload && <button className="btn btn--primary btn--sm" onClick={onUpload}><Icon name="upload" size={14} />{uploadLabel}</button>}
      </div>

      {state == null || state.loading ? (
        <Loading title="불러오는 중…" sub="버전 목록을 가져오고 있습니다." />
      ) : state.error ? (
        <ErrorState error={state.error} title="목록을 불러오지 못했습니다" onRetry={onRetry} onRelogin={onRelogin} />
      ) : rows.length === 0 ? (
        <EmptyState icon="storage" title="객체가 없습니다" sub={emptySub || '이 prefix 에 업로드된 버전이 없습니다.'} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {pageRows.map(row => (
            <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, background: row.isLatest ? 'var(--brand-50)' : 'var(--ink-50, #f7f8fa)', border: '1px solid ' + (row.isLatest ? 'var(--brand-100)' : 'transparent') }}>
              <Icon name={state.data.kind === 'firmware' ? 'control' : 'download'} size={16} />
              <div style={{ minWidth: 0, flex: 1 }}>
                {/* minHeight = .chip 높이(font 11.5 + padding 5px). ★latest·검증중 칩이
                    붙은 행만 이름 줄이 커져서 페이지를 넘길 때마다 패널 높이가 튀었다. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', minHeight: 26 }}>
                  <span className="mono" style={{ fontSize: 13, fontWeight: 700, wordBreak: 'break-all' }}>{row.name}</span>
                  {row.isLatest && <span className="chip" style={{ background: 'var(--brand-600)', color: '#fff', fontWeight: 700 }}>★ latest</span>}
                  {row.key === candidateKey && <span className="chip" style={{ background: '#fff4e5', color: '#b4690e' }}>검증중</span>}
                  {row.type && <span className="chip" style={{ background: 'var(--ink-100)', color: 'var(--ink-600)' }}>{row.type}</span>}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-400)', marginTop: 2 }}>
                  {humanSize(row.size)} · {fmtDateTime(row.lastModified)}{row.sidecar ? ' · sha256 ✓' : ''}
                </div>
              </div>
              {row.canSetLatest && !row.isLatest && (
                <button className="btn btn--ghost btn--sm" disabled={!isDeveloper}
                  title={isDeveloper ? '이 버전을 latest 로' : '개발자 권한이 필요합니다'}
                  onClick={() => onSetLatest(row, state.data.kind)}>
                  <Icon name="undo" size={14} />latest 로
                </button>
              )}
              <button className="icon-btn" onClick={() => onDownload(row)} title="다운로드"><Icon name="download" size={15} /></button>
              <button className="icon-btn icon-btn--danger" disabled={row.isReferenced}
                title={row.isReferenced ? 'latest 가 참조 중 — 다른 버전을 latest 로 변경 후 삭제하세요' : '삭제'}
                style={row.isReferenced ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
                onClick={() => { if (!row.isReferenced) onDelete(row); }}>
                <Icon name="trash" size={15} />
              </button>
            </div>
          ))}
          <Pager page={page} totalPages={totalPages} onPage={setPage} />
        </div>
      )}
    </section>
  );
}

/* 좌우 분리 패널용 — 같은 fetch 결과를 술어로 갈라 하위 state 를 만든다(요청 추가 없음).
   앱은 platform('windows'|'mac'), 펌웨어는 type('robot'|'camera') 으로 나눈다.
   로딩/에러 상태는 그대로 넘겨 두 패널이 같은 안내를 보여 준다.
   totalSize 는 이 패널의 행 합계 — 전체 prefix 합계(사이드카·manifest 포함)와 다르다. */
function sliceRows(state, pred) {
  if (!state || !state.data) return state;
  const rows = state.data.rows.filter(pred);
  return { data: { ...state.data, rows, totalSize: rows.reduce((n, r) => n + (r.size || 0), 0) } };
}

/* ============ 버전 관리 ============ */
export function VersionsAdmin() {
  const toast = useToast();
  const { mode, getCredentials, logout, isDeveloper } = useSession();
  const [app, setApp] = useState(null);     // null=loading | {loading} | {error} | {data}
  const [fw, setFw] = useState(null);
  const [del, setDel] = useState(null);     // 삭제 대상 row
  const [delBusy, setDelBusy] = useState(false);
  const [latest, setLatest] = useState(null);   // { row, kind }
  const [latestBusy, setLatestBusy] = useState(false);
  const [appUp, setAppUp] = useState(null);      // 앱 업로드 모달 — null | 'windows' | 'mac'
  const [fwUp, setFwUp] = useState(null);        // 펌웨어 업로드 모달 — null | 'robot' | 'camera'
  const [upBusy, setUpBusy] = useState(false);
  const [upStep, setUpStep] = useState('');

  const loadApp = useCallback(() => {
    setApp({ loading: true });
    listApp(getCredentials).then(d => setApp({ data: d })).catch(e => setApp({ error: e }));
  }, [getCredentials]);
  const loadFw = useCallback(() => {
    setFw({ loading: true });
    listFirmware(getCredentials).then(d => setFw({ data: d })).catch(e => setFw({ error: e }));
  }, [getCredentials]);
  const loadAll = useCallback(() => { loadApp(); loadFw(); }, [loadApp, loadFw]);
  useEffect(() => {
    if (mode === 'fake') seedFake();
    loadAll();
  }, [mode, loadAll]);

  const remove = async () => {
    setDelBusy(true);
    try {
      await deleteRegistryObject(getCredentials, del.key);
      toast('객체를 삭제했습니다');
      setDel(null);
      if (del.key.startsWith('app/')) loadApp(); else loadFw();
    } catch (e) {
      toast('삭제 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally { setDelBusy(false); }
  };

  const applyLatest = async ({ version, notes, sha256 }) => {
    setLatestBusy(true);
    const { row, kind } = latest;
    try {
      if (kind === 'firmware') {
        await setFirmwareLatest(getCredentials, { key: row.key, type: row.type, version, notes, sha256, releasedAt: row.lastModified });
        toast('펌웨어 latest 를 변경했습니다');
        setLatest(null); loadFw();
      } else {
        await setAppLatest(getCredentials, { key: row.key, version, notes, sha256, releasedAt: row.lastModified });
        toast('앱 latest 를 변경했습니다');
        setLatest(null); loadApp();
      }
    } catch (e) {
      toast('변경 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally { setLatestBusy(false); }
  };

  const doAppUpload = async ({ platform, fileObj, fileName, version, notes }) => {
    setUpBusy(true); setUpStep('');
    try {
      const fileBuffer = await fileObj.arrayBuffer();
      await uploadAppVersion(getCredentials, { platform, fileName, fileBuffer, version, notes, onStep: setUpStep });
      toast(`${platform === 'mac' ? 'macOS' : 'Windows'} 새 버전을 등록했습니다 (새 latest)`);
      setAppUp(null); loadApp();
    } catch (e) {
      toast('업로드 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally { setUpBusy(false); setUpStep(''); }
  };

  const doFwUpload = async ({ type, fileObj, version, notes }) => {
    setUpBusy(true); setUpStep('');
    try {
      const fileBuffer = await fileObj.arrayBuffer();
      await uploadFirmware(getCredentials, { type, version, notes, fileBuffer, onStep: setUpStep });
      toast('펌웨어 새 버전을 업로드했습니다');
      setFwUp(null); loadFw();
    } catch (e) {
      toast('업로드 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally { setUpBusy(false); setUpStep(''); }
  };

  const download = async (row) => {
    if (mode === 'fake') { toast('데모(오프라인) 모드에서는 실제 파일 다운로드를 지원하지 않습니다.'); return; }
    try {
      await downloadObject(objectUrl(row.key), (row.name || row.key).split('/').pop());
    } catch (e) {
      toast('다운로드 실패: ' + (e && e.message ? e.message : e), 'error');
    }
  };

  // 펌웨어는 기기 type 으로 좌우 분리. firmware/{type}/ 은 관례일 뿐이라 robot/camera 가
  // 아닌 prefix 도 있을 수 있다 — 그런 행은 사라지지 않게 아래에 '기타' 패널로 남긴다.
  const isOtherFw = (r) => r.type !== 'robot' && r.type !== 'camera';
  const otherFwCount = fw && fw.data ? fw.data.rows.filter(isOtherFw).length : 0;

  // 403(IAM 미적용) 은 두 패널 공통 — 상단 안내 배너 1회.
  const iam403 = (app && app.error && app.error.status === 403) || (fw && fw.error && fw.error.status === 403);

  return (
    <div>
      {iam403 && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', marginBottom: 16, borderRadius: 10, background: '#fff4e5', border: '1px solid #f6d9a8', color: '#8a5200' }}>
          <Icon name="lock" size={18} />
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <b>레지스트리 버킷 권한이 없습니다.</b> 목록(ListBucket)·삭제·latest 변경은 Identity Pool developer 역할에
            IAM 정책이 적용돼야 합니다. <span className="mono">plan.md §15 (버전 관리 IAM 정책)</span> 의 JSON 을 한 번 적용하세요.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button className="btn btn--ghost btn--sm" onClick={loadAll} title="앱·펌웨어 목록을 모두 다시 읽습니다">
          <Icon name="refresh" size={14} />전체 새로고침
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 18, alignItems: 'start', marginBottom: 18 }}>
        <VersionPanel title="앱 설치파일 · Windows (app/)" icon="download"
          state={sliceRows(app, r => r.platform === 'windows')} isDeveloper={isDeveloper}
          onRetry={loadApp} onRelogin={logout}
          onDelete={setDel} onSetLatest={(row, kind) => setLatest({ row, kind })}
          onDownload={download} onUpload={() => setAppUp('windows')} uploadLabel="새 버전 등록"
          emptySub="app/ 에 업로드된 Windows 설치파일이 없습니다." style={{ marginBottom: 0 }} />

        <VersionPanel title="앱 설치파일 · macOS (app/mac/)" icon="download"
          state={sliceRows(app, r => r.platform === 'mac')} isDeveloper={isDeveloper}
          onRetry={loadApp} onRelogin={logout}
          onDelete={setDel} onSetLatest={(row, kind) => setLatest({ row, kind })}
          onDownload={download} onUpload={() => setAppUp('mac')} uploadLabel="새 버전 등록"
          emptySub="app/mac/ 에 업로드된 macOS 설치파일이 없습니다." style={{ marginBottom: 0 }} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 18, alignItems: 'start' }}>
        <VersionPanel title="펌웨어 · 로봇팔 (firmware/robot/)" icon="control"
          state={sliceRows(fw, r => r.type === 'robot')} isDeveloper={isDeveloper}
          onRetry={loadFw} onRelogin={logout}
          onDelete={setDel} onSetLatest={(row, kind) => setLatest({ row, kind })}
          onDownload={download} onUpload={() => setFwUp('robot')} uploadLabel="새 버전 업로드"
          emptySub="firmware/robot/ 에 업로드된 펌웨어가 없습니다." style={{ marginBottom: 0 }} />

        <VersionPanel title="펌웨어 · AI 카메라 (firmware/camera/)" icon="control"
          state={sliceRows(fw, r => r.type === 'camera')} isDeveloper={isDeveloper}
          onRetry={loadFw} onRelogin={logout}
          onDelete={setDel} onSetLatest={(row, kind) => setLatest({ row, kind })}
          onDownload={download} onUpload={() => setFwUp('camera')} uploadLabel="새 버전 업로드"
          emptySub="firmware/camera/ 에 업로드된 펌웨어가 없습니다." style={{ marginBottom: 0 }} />
      </div>

      {otherFwCount > 0 && (
        <VersionPanel title="펌웨어 · 기타 type" icon="control"
          state={sliceRows(fw, isOtherFw)} isDeveloper={isDeveloper}
          onRetry={loadFw} onRelogin={logout}
          onDelete={setDel} onSetLatest={(row, kind) => setLatest({ row, kind })}
          onDownload={download} style={{ marginTop: 18 }} />
      )}

      {del && <Confirm title="객체를 삭제할까요?"
        body={<><b className="mono">{del.key}</b> 객체를 영구 삭제합니다. 되돌릴 수 없습니다.</>}
        confirmLabel={delBusy ? '삭제 중…' : '삭제'}
        onConfirm={delBusy ? () => {} : remove} onClose={delBusy ? () => {} : () => setDel(null)} />}

      {latest && <SetLatestModal row={latest.row} kind={latest.kind} busy={latestBusy}
        onSubmit={applyLatest} onClose={latestBusy ? () => {} : () => setLatest(null)} />}

      {appUp && <AppUploadModal busy={upBusy} step={upStep} platform={appUp}
        onSubmit={doAppUpload} onClose={upBusy ? () => {} : () => setAppUp(null)} />}

      {fwUp && <FirmwareUploadModal busy={upBusy} step={upStep} defaultType={fwUp}
        onSubmit={doFwUpload} onClose={upBusy ? () => {} : () => setFwUp(null)} />}
    </div>
  );
}

export default VersionsAdmin;
